import { Injectable, inject, OnDestroy } from '@angular/core';
import { FCMService } from '@shared';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { takeUntil, filter, distinctUntilChanged } from 'rxjs/operators';
import { Subject, firstValueFrom } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '../../environments/environment';
import { ApiNotificationsService, AuthService } from '@shared';

/**
 * Plays notification sound when the user app receives a push notification (foreground).
 * Works on Windows, Mac, and phone when the app is open and visible.
 *
 * Chrome/Edge require a user gesture before Web Audio can play. We unlock on first click/touch.
 *
 * FCM: tokens are sent to `POST /notifications/register-token`, which appends to `users.fcmTokens`
 * (multi-device). Web uses FCMService; native uses Capacitor PushNotifications.
 */
@Injectable({ providedIn: 'root' })
export class UserNotificationSoundService implements OnDestroy {
  private readonly fcmService = inject(FCMService);
  private readonly authService = inject(AuthService);
  private readonly apiNotifications = inject(ApiNotificationsService);
  private readonly destroy$ = new Subject<void>();
  /** Latest native FCM token (Capacitor); used to re-register after login / resume. */
  private lastNativeToken: string | null = null;
  private readonly profile$ = toObservable(this.authService.profile);

  private initialized = false;
  private lastPlayedAt = 0;
  private readonly MIN_INTERVAL_MS = 1000;

  /** AudioContext unlocked on first user gesture (required by Chrome autoplay policy) */
  private audioContext: AudioContext | null = null;

  /**
   * Call from app init to start listening for notifications and play sound.
   */
  start(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.setupAudioUnlock();

    if (Capacitor.isNativePlatform()) {
      void this.initializeNative();
    } else {
      void this.initializeWeb();
    }
  }

  /**
   * Native (Android/iOS): use @capacitor/push-notifications for reliable background delivery.
   */
  private async initializeNative(): Promise<void> {
    try {
      let permission = await PushNotifications.checkPermissions();
      if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
        permission = await PushNotifications.requestPermissions();
      }
      if (permission.receive !== 'granted') {
        console.warn('[UserPush] Native push permission denied');
        return;
      }

      await PushNotifications.addListener('registration', async (token) => {
        const raw = token?.value ?? '';
        console.log('[UserPush] Native FCM token received');
        this.lastNativeToken = raw || null;
        await this.saveTokenToBackend(raw);
      });

      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        this.playAlert();
        this.showNativeNotificationPayload(notification);
      });

      await PushNotifications.register();

      // If token arrived before JWT profile, or user logs in after register(), sync to users.fcmTokens[].
      this.subscribeNativeTokenBackendSync();
    } catch (err) {
      console.error('[UserPush] Native initialization failed:', err);
    }
  }

  /**
   * Web: Firebase web SDK via FCMService.
   */
  private async initializeWeb(): Promise<void> {
    this.fcmService.setVapidKey(environment.fcmVapidKey || null);
    await this.fcmService.initialize();

    // Request permission early (better UX — before login).
    await this.fcmService.requestNotificationPermission();

    // Listen for foreground messages.
    this.fcmService
      .onMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        const now = Date.now();
        if (now - this.lastPlayedAt < this.MIN_INTERVAL_MS) return;
        this.lastPlayedAt = now;
        this.playAlert();
        this.showNotification(payload);
      });

    // Register FCM token with backend whenever user logs in (Gap 1 fix).
    this.fcmService
      .initializeForUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (token) => {
          if (token) console.log('[UserPush] FCM token registered with backend');
        },
        error: (err) => console.warn('[UserPush] FCM token registration failed:', err),
      });
  }

  /**
   * When profile appears after the native registration event, POST token again (server dedupes).
   */
  private subscribeNativeTokenBackendSync(): void {
    const trySave = async () => {
      const t = this.lastNativeToken;
      if (!t?.trim()) return;
      await this.saveTokenToBackend(t);
    };

    this.profile$
      .pipe(
        filter((p): p is NonNullable<typeof p> => !!p?.id),
        distinctUntilChanged((a, b) => a.id === b.id),
        takeUntil(this.destroy$),
      )
      .subscribe(() => void trySave());
  }

  /**
   * Idempotent: re-POST current device token so `users.fcmTokens` stays correct (multi-device safe).
   */
  async ensureTokenRegistered(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      const t = this.lastNativeToken;
      if (t?.trim() && this.authService.profile()?.id) {
        await this.saveTokenToBackend(t);
      }
      return;
    }
    await this.fcmService.ensureTokenRegistered();
  }

  private async saveTokenToBackend(token: string): Promise<void> {
    const profile = this.authService.profile();
    if (!profile?.id || !token?.trim()) return;
    try {
      await firstValueFrom(this.apiNotifications.registerFCMToken(token));
      console.log('[UserPush] Native FCM token saved to backend');
    } catch (err) {
      console.warn('[UserPush] Native FCM token save failed:', err);
    }
  }

  private showNativeNotificationPayload(notification: any): void {
    this.showNotification({
      notification: { title: notification.title, body: notification.body },
      data: notification.data,
    });
  }

  private setupAudioUnlock(): void {
    if (typeof document === 'undefined') return;
    const events = ['click', 'touchstart', 'keydown'];
    const doUnlock = () => {
      this.unlockAndPrime();
      events.forEach((e) => document.removeEventListener(e, doUnlock));
    };
    events.forEach((e) => document.addEventListener(e, doUnlock, { once: true, passive: true }));
  }

  private unlockAndPrime(): void {
    if (this.audioContext?.state === 'running') return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      this.audioContext = ctx;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.01, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch {
      // ignore
    }
  }

  private playAlert(): void {
    if (typeof window === 'undefined') return;
    const ctx = this.audioContext;
    if (ctx && ctx.state !== 'closed') {
      try {
        if (ctx.state === 'suspended') {
          ctx.resume().then(() => this.doPlayWebAudio(ctx));
        } else {
          this.doPlayWebAudio(ctx);
        }
        return;
      } catch {
        // fall through
      }
    }
    this.doPlayHtml5Audio();
  }

  private doPlayWebAudio(ctx: AudioContext): void {
    try {
      const playTone = (freq: number, startTime: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.2, startTime);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        osc.start(startTime);
        osc.stop(startTime + duration);
      };
      playTone(880, 0, 0.2);
      playTone(1100, 0.3, 0.25);
    } catch {
      this.doPlayHtml5Audio();
    }
  }

  private doPlayHtml5Audio(): void {
    try {
      const wav = this.createBeepWav(880, 0.4);
      const audio = new Audio(wav);
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch {
      // ignore
    }
  }

  private createBeepWav(freq: number, duration: number): string {
    const sampleRate = 44100;
    const numSamples = Math.floor(sampleRate * duration);
    const numChannels = 1;
    const bitsPerSample = 16;
    const blockAlign = numChannels * (bitsPerSample / 8);
    const byteRate = sampleRate * blockAlign;
    const dataSize = numSamples * blockAlign;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    let offset = 0;
    const writeStr = (s: string) => {
      for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
    };
    writeStr('RIFF');
    view.setUint32(offset, 36 + dataSize, true);
    offset += 4;
    writeStr('WAVE');
    writeStr('fmt ');
    view.setUint32(offset, 16, true);
    offset += 4;
    view.setUint16(offset, 1, true);
    offset += 2;
    view.setUint16(offset, numChannels, true);
    offset += 2;
    view.setUint32(offset, sampleRate, true);
    offset += 4;
    view.setUint32(offset, byteRate, true);
    offset += 4;
    view.setUint16(offset, blockAlign, true);
    offset += 2;
    view.setUint16(offset, bitsPerSample, true);
    offset += 2;
    writeStr('data');
    view.setUint32(offset, dataSize, true);
    offset += 4;
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const sample = Math.sin(2 * Math.PI * freq * t) * 32767 * 0.25;
      view.setInt16(offset, Math.max(-32768, Math.min(32767, sample)), true);
      offset += 2;
    }
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return 'data:audio/wav;base64,' + btoa(binary);
  }

  /**
   * Display a browser notification for foreground messages
   */
  private showNotification(payload: any): void {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    // Check if permission is granted
    if (Notification.permission !== 'granted') {
      console.warn('[Notification] Permission not granted, cannot show notification');
      return;
    }

    const notification = payload.notification || {};
    const title = notification.title || 'CartCo';
    const body = notification.body || '';
    const icon = notification.icon || '/favicon.ico';
    const image = notification.image || payload.fcmOptions?.image || undefined;

    const options: NotificationOptions = {
      body,
      icon,
      tag: 'cartco-notification',
      requireInteraction: false,
      silent: false,
      vibrate: [200, 100, 200],
      badge: '/favicon.ico',
      data: payload.data || {},
      // image is supported by the Web Notification API but not in TS lib.dom NotificationOptions
      ...(image ? { image } : {}),
    } as NotificationOptions;

    try {
      const notificationInstance = new Notification(title, options);
      
      // Handle notification click
      notificationInstance.onclick = (event) => {
        event.preventDefault();
        window.focus();
        
        // Handle navigation if data contains route
        if (payload.data?.type) {
          // You can add routing logic here based on notification type
          // For example: this.router.navigate(['/orders', payload.data.orderId]);
        }
        
        notificationInstance.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notificationInstance.close();
      }, 5000);
    } catch (error) {
      console.error('[Notification] Error showing notification:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.audioContext?.state !== 'closed') {
      this.audioContext?.close();
    }
  }
}
